/* GrowthOS — Contact record page.
   Re-skinned to match the Lead detail (viewLead.jsx): `.gos-leadrec` shell with a
   back row, header card (avatar, name, contact buttons, dots menu, chips), a
   horizontal tab bar and a read-only Overview + sticky Details panel. Tabs reuse the
   SHARED child components (Timeline, Notes, Attachments, ChildCampaign, ChildDeal,
   Email). Tags use the real Contact tag APIs. */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchAttachmentsAndAppendToModule,
  fetchCampaignsAndAppendToModule,
  fetchDataAndUpdateStore,
  fetchDetailAndAppendToModule,
  fetchEmailAndAppendToModule,
  fetchNotesAndAppendToModule,
  fetchTagsAndAppendToModule,
} from '../../redux/actions';
import {
  getContactById,
  getGridDataByModuleKey,
  getTagsByModule,
} from '../../redux/selector';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import apiServices from '../../services/service';
import { contactInputData } from '../../shared/constants';
import Loader from '../../shared/component/Loader';
import Notes from '../sharedChild/note/notes';
import Attachments from '../sharedChild/attachment/attachments';
import Email from '../sharedChild/email/emails';
import ChildCampaign from '../sharedChild/childCampaign';
import Opportunities from '../sharedChild/opportunity/opportunities';
import Timeline from '../sharedChild/timeline';
import ComposeEmail from '../sharedChild/email/compose';
import Enrichment from '../leads/leadEnrichment';
import LeadCallModal from '../leads/LeadCallModal';
import LeadWhatsAppModal from '../leads/LeadWhatsAppModal';

/* ---------- GrowthOS record icons (stroke, 24×24) ---------- */
const ICONS = {
  grid: <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />,
  clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  note: <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5" />,
  opportunity: <path d="M3 3v18h18M7 14l3-3 4 4 5-6" />,
  paperclip: <path d="M21 8l-9 9a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8-8" />,
  campaigns: <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1ZM15 8a4 4 0 0 1 0 8M18 5a8 8 0 0 1 0 14" />,
  mail: <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7" />,
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  users: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" /></>),
  location: (<><path d="M12 21s-7-6.5-7-11a7 7 0 0 1 14 0c0 4.5-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></>),
  info: (<><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></>),
  briefcase: <path d="M3 7h18v13H3zM8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />,
  phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.6a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.5-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.6 2.6.7a2 2 0 0 1 1.7 2z" />,
  whatsapp: <path d="M12 2a9.9 9.9 0 0 0-8.4 15.2L2 22l4.9-1.5A10 10 0 1 0 12 2zM9 8c.3-.8 1.5-.8 1.8 0l.5 1.3c.1.4 0 .8-.3 1l-.6.5c.6 1.2 1.4 2 2.6 2.6l.5-.6c.2-.3.6-.4 1-.3l1.3.5c.8.3.8 1.5 0 1.8-1.8.8-4 .3-5.8-1.5S8.2 9.8 9 8z" />,
  dots: <path d="M12 5h.01M12 12h.01M12 19h.01" />,
  chevL: <path d="M15 18l-6-6 6-6" />,
  edit: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />,
  zap: <path d="M13 2L3 14h7l-1 8 10-12h-7z" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  globe: (<><circle cx="12" cy="12" r="9" /><path d="M2.5 12h19M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" /></>),
  tag: (<><path d="M20.6 13.4 11 3.8a2 2 0 0 0-1.4-.6H4a1 1 0 0 0-1 1v5.6a2 2 0 0 0 .6 1.4l9.6 9.6a2 2 0 0 0 2.8 0l4.6-4.6a2 2 0 0 0 0-2.8z" /><path d="M7.5 7.5h.01" /></>),
  link: <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5" />,
  user: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  plus: <path d="M12 5v14M5 12h14" />,
  search: (<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>),
  check: <path d="M20 6 9 17l-5-5" />,
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name] || null}
  </svg>
);

const GOS_AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const gosInitials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const gosAvaColor = (name) =>
  GOS_AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GOS_AVA_COLORS.length];

const val = (v) => (v === 0 || v ? v : null);

const InfoCard = ({ title, icon, rows }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className="gos-lr-card-rows">
      {rows.map(([label, value], i) => (
        <div className="gos-lr-drow" key={i}>
          <div className="gos-lr-dlabel">{label}</div>
          <div className={'gos-lr-dval' + (val(value) ? '' : ' muted')}>{val(value) ?? '—'}</div>
        </div>
      ))}
    </div>
  </div>
);
const DetailRow = ({ icon, label, children }) => (
  <div className="gos-lr-drow">
    <div className="gos-lr-dlabel">
      <Ic name={icon} size={15} /> {label}
    </div>
    <div className="gos-lr-dval gos-lr-dval-right">{children ?? '—'}</div>
  </div>
);
const Placeholder = ({ icon, title, text }) => (
  <div className="gos-lr-card gos-lr-placeholder">
    <div className="gos-lr-ph-ic">
      <Ic name={icon} size={22} />
    </div>
    <div className="gos-lr-ph-title">{title}</div>
    <div className="gos-lr-ph-text">{text}</div>
  </div>
);

/* ---------- Contact tags editor (add / remove / create) ---------- */
const TAG_TONES = ['blue', 'green', 'amber', 'purple', 'rose', 'gray', 'red'];
const toneFor = (name) =>
  TAG_TONES[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % TAG_TONES.length];
const tagName = (t) => (Array.isArray(t?.name) ? t.name.join(', ') : t?.name || '');

const ContactTagEditor = ({ contact }) => {
  const dispatch = useDispatch();
  const library = useSelector(getTagsByModule('contactList')) || [];
  const applied = contact?.tags || [];
  const moduleId = contact?.id;

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchDataAndUpdateStore({ key: 'tagsByModule', inputData: 'Contact', storeKey: 'contactList' }));
  }, [dispatch]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQ('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open]);

  const refreshTags = () => {
    dispatch(fetchTagsAndAppendToModule({ moduleName: 'Contact', id: moduleId }));
    dispatch(fetchDataAndUpdateStore({ key: 'tagsByModule', inputData: 'Contact', storeKey: 'contactList' }));
  };

  const addTagByName = async (name) => {
    if (!moduleId || busy) return;
    setBusy(true);
    try {
      const res = await apiServices.createTags({ tagName: [name], module: 'Contact', moduleId: [moduleId] });
      if (res.status === 200) {
        toast.success('Tag added successfully!', { position: 'top-right' });
        refreshTags();
      }
    } catch (err) {
      console.error('Error adding tag:', err);
      toast.error('Failed to add tag.', { position: 'top-right' });
    } finally {
      setBusy(false);
    }
  };

  const removeTag = async (tag) => {
    if (!moduleId || busy) return;
    setBusy(true);
    try {
      const res = await apiServices.deleteTagsByModuleId({ id: [tag.id], module: 'Contact', moduleId: [moduleId] });
      if (res.status === 200) {
        toast.success('Tag removed successfully!', { position: 'top-right' });
        refreshTags();
      }
    } catch (err) {
      console.error('Error removing tag:', err);
      toast.error('Failed to remove tag.', { position: 'top-right' });
    } finally {
      setBusy(false);
    }
  };

  const toggleLibraryTag = (libTag) => {
    const name = tagName(libTag);
    const existing = applied.find((a) => tagName(a) === name);
    if (existing) removeTag(existing);
    else addTagByName(name);
  };

  const ql = q.trim();
  const filtered = ql ? library.filter((t) => tagName(t).toLowerCase().includes(ql.toLowerCase())) : library;
  const exact = library.some((t) => tagName(t).toLowerCase() === ql.toLowerCase());
  const nextTone = TAG_TONES[library.length % TAG_TONES.length];

  const create = () => {
    if (!ql || exact || busy) return;
    addTagByName(ql);
    setQ('');
    inputRef.current?.focus();
  };

  return (
    <div className="gos-lead-tags">
      <span className="gos-lead-tags-label">
        <Ic name="tag" size={13} /> Tags
      </span>
      {applied.map((t) => {
        const tone = toneFor(tagName(t));
        return (
          <span
            key={t.id}
            className="gos-lead-tag"
            style={{ background: `var(--gos-${tone}-bg)`, color: `var(--gos-${tone}-fg)` }}
          >
            <span className="gos-lt-dot" />
            {tagName(t)}
            <button type="button" className="gos-lt-x" title="Remove tag" onClick={() => removeTag(t)} disabled={busy}>
              <Ic name="x" size={13} />
            </button>
          </span>
        );
      })}
      <div className="gos-tag-add-wrap" ref={wrapRef}>
        <button type="button" className={'gos-tag-add' + (open ? ' on' : '')} onClick={() => setOpen((o) => !o)}>
          <Ic name="plus" size={14} /> Add tag
        </button>
        {open ? (
          <div className="gos-tag-pop">
            <div className="gos-tag-pop-search">
              <Ic name="search" size={14} />
              <input
                ref={inputRef}
                placeholder="Search or create…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && ql && !exact) create();
                }}
              />
            </div>
            <div className="gos-tag-pop-label">{ql ? 'Matching tags' : 'Apply tags'}</div>
            <div className="gos-tag-pop-list">
              {filtered.length === 0 && !ql ? <div className="gos-tag-pop-empty">No tags yet</div> : null}
              {filtered.map((t) => {
                const name = tagName(t);
                const on = applied.some((a) => tagName(a) === name);
                const tone = toneFor(name);
                return (
                  <button
                    type="button"
                    key={t.id}
                    className={'gos-tag-opt' + (on ? ' on' : '')}
                    onClick={() => toggleLibraryTag(t)}
                    disabled={busy}
                  >
                    <span className="gos-to-dot" style={{ background: `var(--gos-${tone}-fg)` }} />
                    {name}
                    <span className="gos-to-check">{on ? <Ic name="check" size={12} /> : null}</span>
                  </button>
                );
              })}
              {ql && filtered.length === 0 ? (
                <div className="gos-tag-pop-empty">No matches — create it below</div>
              ) : null}
            </div>
            {ql && !exact ? (
              <button type="button" className="gos-tag-create" onClick={create} disabled={busy}>
                <span
                  className="gos-tc-swatch"
                  style={{ background: `var(--gos-${nextTone}-bg)`, border: `1px solid var(--gos-${nextTone}-fg)` }}
                />
                Create <strong>{`“${ql}”`}</strong>
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ViewContact = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const stateData = useSelector((state) => state);
  const storeLoading = isLoadingByKey(stateData, 'contactList');
  const [loading, setLoading] = useState(storeLoading);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [emailOpen, setEmailOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [opportunityByModule, setOpportunityByModule] = useState({});
  const [enrichedDataLoading, setEnrichedDataLoading] = useState(false);
  const [showPopupContact, setShowPopupContact] = useState(false);
  const [conatctEnrichedData, setContactEnrichedData] = useState();
  const [isEnrichedFlag, setIsEnrichedFlag] = useState(false);
  const [refreshView, setRefreshView] = useState(false);
  const gridData = useSelector(getGridDataByModuleKey('contactList'));

  const searchParams = new URLSearchParams(location.search);
  const contactID = searchParams.get('id');

  let hasUpdateOpportunityPermission = true;
  let hasViewOpportunityPermission = true;
  let hasUpdateCampaignPermission = true;
  let hasViewCampaignPermission = true;
  const userUIControlPermission = JSON.parse(localStorage.getItem('userUIControlPermission'))?.campaignmenu;
  const userUIControlPermissionOpportunity = JSON.parse(localStorage.getItem('userUIControlPermission'))?.opportunitymenu;
  if (userUIControlPermission) {
    hasViewCampaignPermission = userUIControlPermission?.campaignviewscreen ? userUIControlPermission.campaignviewscreen.canView : true;
    hasUpdateCampaignPermission = userUIControlPermission?.campaignupdatescreen ? userUIControlPermission.campaignupdatescreen.canView : true;
  }
  if (userUIControlPermissionOpportunity) {
    hasViewOpportunityPermission = userUIControlPermissionOpportunity?.opportunityviewscreen ? userUIControlPermissionOpportunity.opportunityviewscreen.canView : true;
    hasUpdateOpportunityPermission = userUIControlPermissionOpportunity?.opportunityupdatescreen ? userUIControlPermissionOpportunity.opportunityupdatescreen.canView : true;
  }

  const [contactLocalData, setContactLocalData] = useState(location.state || {});
  const contactStoreData = useSelector((state) => getContactById(state, location.state?.id || contactID));

  useEffect(() => {
    setContactLocalData((prev) => ({ ...prev, ...contactStoreData }));
  }, [contactStoreData]);

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prev) => ({ ...prev, [name]: selectedOption }));
  };

  const handleOpenEditForm = () => {
    navigate('/contacts/editContact', { state: { ...contactLocalData, type: 'view', from: 'view' } });
  };

  const openPopupContact = () => setShowPopupContact(true);
  const closePopupContact = () => setShowPopupContact(false);

  const handleClickDataEnrichment = () => {
    if (!contactLocalData.firstName || !contactLocalData.lastName) {
      toast.error('Please fill in the first name and last name fields.', { position: 'top-right' });
      return;
    }
    setEnrichedDataLoading(true);
    const data = {
      LeadName: `${contactLocalData?.fullName}`,
      CompanyName: `${contactLocalData?.account}`,
      EnrichmentType: 'contacts',
    };
    const fetchEnrichedData = () => {
      apiServices
        .getEnrichedData(data)
        .then((response) => {
          if (response.status === 200) {
            setContactEnrichedData(response.data?.enriched_data);
            setEnrichedDataLoading(false);
            openPopupContact();
            apiServices
              .updateEnrichedFlag({ id: contactLocalData.id }, 'contact')
              .then((res) => {
                if (res.status === 200) setIsEnrichedFlag(true);
              })
              .catch((error) => {
                console.error('Error updating enriched flag:', error);
                toast.error('Failed to update enriched flag.', { position: 'top-right' });
              });
          } else if (response.status === 202) {
            toast.info('Enrichment Initiated! Retrying in 2 minutes...', { position: 'top-right' });
            setTimeout(fetchEnrichedData, 120000);
          }
        })
        .catch((error) => {
          console.error('Error while fetching enriched data:', error);
          const errorMsg = error?.response?.data?.error || 'An unexpected error occurred.';
          toast.error(errorMsg, { position: 'top-right' });
          setEnrichedDataLoading(false);
        });
    };
    fetchEnrichedData();
  };

  const handleUpdateForm = async (formData) => {
    setLoading(true);
    try {
      const response = await apiServices.updateContactList(formData);
      if (response.status === 200) {
        await dispatch(
          fetchDataAndUpdateStore({
            key: 'contactList',
            inputData: gridData?.inputPayload ? gridData?.inputPayload : contactInputData,
          })
        );
        closePopupContact();
        setRefreshView((prev) => !prev);
      } else {
        setLoading(false);
        const errorMessages = [];
        for (const key in response.errors) {
          if (response.errors.hasOwnProperty(key)) errorMessages.push(...response.errors[key]);
        }
        errorMessages.forEach((m) => toast.error(m, { position: 'top-right' }));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An unexpected error occurred.', { position: 'top-right' });
      setLoading(false);
    }
  };

  // opportunities (ChildDeal) for this contact
  useEffect(() => {
    if (!contactLocalData?.id) return;
    setLoading(true);
    apiServices
      .getOpportunityByModule({ moduleName: 'Contact', id: contactLocalData?.id })
      .then((response) => {
        if (response.status === 200) setOpportunityByModule(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching opportunities:', error);
        setLoading(false);
      });
  }, [contactLocalData?.id]);

  useEffect(() => {
    if (contactLocalData?.isEnriched) setIsEnrichedFlag(contactLocalData?.isEnriched);
  }, [contactLocalData]);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    const contactId = location.state?.id || contactID;
    if (contactId) {
      dispatch(fetchDetailAndAppendToModule({ moduleName: 'Contact', id: contactId, localDetail: location.state || null }));
      dispatch(fetchNotesAndAppendToModule({ moduleName: 'Contact', id: contactId }));
      dispatch(fetchAttachmentsAndAppendToModule({ moduleName: 'Contact', id: contactId }));
      dispatch(fetchCampaignsAndAppendToModule({ moduleName: 'Contact', id: contactId }));
      dispatch(fetchEmailAndAppendToModule({ moduleName: 'Contact', id: contactId }));
      dispatch(fetchTagsAndAppendToModule({ moduleName: 'Contact', id: contactId }));
    }
  }, [location, contactLocalData?.id, refreshView]);

  const cd = contactLocalData || {};
  const fullName = cd.fullName || `${cd.firstName || ''} ${cd.lastName || ''}`.trim() || 'Contact';
  const cityState = [cd.city, cd.state].filter(Boolean).join(', ');
  const chips = [
    cd.leadSource && { icon: 'campaigns', label: cd.leadSource },
    cityState && { icon: 'location', label: cityState },
    cd.contactPersonRole && { icon: 'user', label: cd.contactPersonRole },
    cd.department && { icon: 'briefcase', label: cd.department },
  ].filter(Boolean);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'grid' },
    { id: 'activities', label: 'Activities', icon: 'clock' },
    { id: 'notes', label: 'Notes', icon: 'note', count: cd.notesList?.length },
    { id: 'attachments', label: 'Attachments', icon: 'paperclip', count: cd.attachments?.length },
    hasViewCampaignPermission && { id: 'campaigns', label: 'Campaigns', icon: 'campaigns', count: cd.campaigns?.length },
    hasViewOpportunityPermission && { id: 'opportunities', label: 'Opportunities', icon: 'opportunity' },
    { id: 'emails', label: 'Emails', icon: 'mail', count: cd.emails?.length },
  ].filter(Boolean);

  const overview = (
    <div className="gos-lr-grid2">
      <InfoCard
        title="Contact Information"
        icon="users"
        rows={[
          ['Full Name', val(fullName)],
          ['Title', val(cd.title)],
          ['Email', val(cd.email)],
          ['Phone', val(cd.phone)],
          ['Mobile', val(cd.mobile)],
          ['Secondary Email', val(cd.secondaryEmail)],
        ]}
      />
      <InfoCard
        title="Company & Role"
        icon="building"
        rows={[
          ['Account Name', val(cd.account)],
          ['Department', val(cd.department)],
          ['Contact Person Role', val(cd.contactPersonRole)],
          ['Salutation', val(cd.salutation)],
          ['Owner', val(cd.owner)],
          ['Lead Source', val(cd.leadSource)],
        ]}
      />
      <InfoCard
        title="Social & More"
        icon="link"
        rows={[
          ['LinkedIn', val(cd.linkedin)],
          ['Twitter', val(cd.twitter)],
          ['Facebook', val(cd.facebook)],
          ['Skype ID', val(cd.skypeId)],
          ['Reporting To', val(cd.reportingTo)],
          ['Email Opt-out', cd.emailOptOut ? 'Yes' : 'No'],
        ]}
      />
      <InfoCard
        title="Address Information"
        icon="location"
        rows={[
          ['Street', val(cd.street)],
          ['City', val(cd.city)],
          ['State', val(cd.state)],
          ['Zip Code', val(cd.zipCode)],
          ['Country', val(cd.country)],
          ['Technology Stack', val(cd.technologyStack)],
        ]}
      />
    </div>
  );

  const detailsPanel = (
    <div className="gos-lr-card gos-lr-sticky">
      <div className="gos-lr-card-head">
        <div className="gos-lr-card-title">
          <Ic name="info" size={16} /> Details
        </div>
        <button type="button" className="gos-lr-iconbtn" onClick={handleOpenEditForm} title="Edit contact">
          <Ic name="edit" size={14} />
        </button>
      </div>
      <div className="gos-lr-sec">
        <div className="gos-lr-sec-title">Contact</div>
        <div className="gos-lr-card-rows">
          <DetailRow icon="user" label="Name">{val(fullName)}</DetailRow>
          <DetailRow icon="briefcase" label="Title">{val(cd.title)}</DetailRow>
          <DetailRow icon="phone" label="Phone">{val(cd.phone || cd.mobile)}</DetailRow>
          <DetailRow icon="mail" label="Email">
            {cd.email ? <span className="gos-lr-link">{cd.email}</span> : '—'}
          </DetailRow>
          <DetailRow icon="location" label="City">{val(cd.city)}</DetailRow>
          <DetailRow icon="globe" label="Region">{val(cd.state)}</DetailRow>
          <DetailRow icon="campaigns" label="Source">{val(cd.leadSource)}</DetailRow>
        </div>
      </div>
      <div className="gos-lr-divider" />
      <div className="gos-lr-sec">
        <div className="gos-lr-sec-title">Company</div>
        <div className="gos-lr-card-rows">
          <DetailRow icon="building" label="Account">{val(cd.account)}</DetailRow>
          <DetailRow icon="briefcase" label="Department">{val(cd.department)}</DetailRow>
          <DetailRow icon="user" label="Role">{val(cd.contactPersonRole)}</DetailRow>
          <DetailRow icon="user" label="Owner">{val(cd.owner)}</DetailRow>
          <DetailRow icon="link" label="LinkedIn">
            {cd.linkedin ? <span className="gos-lr-link">{cd.linkedin}</span> : '—'}
          </DetailRow>
        </div>
      </div>
    </div>
  );

  let tabBody;
  if (activeTab === 'overview') tabBody = overview;
  else if (activeTab === 'activities') tabBody = <Timeline localData={contactLocalData} module={'Contact'} />;
  else if (activeTab === 'notes') tabBody = <Notes leadLocalData={contactLocalData} module={'Contact'} />;
  else if (activeTab === 'attachments') tabBody = <Attachments localData={contactLocalData} module={'Contact'} />;
  else if (activeTab === 'campaigns')
    tabBody = hasViewCampaignPermission ? (
      <ChildCampaign localData={contactLocalData} module={'Contact'} hasUpdateCampaignPermission={hasUpdateCampaignPermission} />
    ) : (
      <Placeholder icon="campaigns" title="Campaigns" text="You don't have permission to view campaigns for this contact." />
    );
  else if (activeTab === 'opportunities')
    tabBody = hasViewOpportunityPermission ? (
      <Opportunities localData={opportunityByModule} module={'Contact'} hasUpdateOpportunityPermission={hasUpdateOpportunityPermission} />
    ) : (
      <Placeholder icon="opportunity" title="Opportunities" text="You don't have permission to view opportunities for this contact." />
    );
  else if (activeTab === 'emails') tabBody = <Email localData={contactLocalData} module={'Contact'} />;

  return (
    <>
      {loading && <Loader />}

      <div className="ViewLeadPageOuter gos-leadrec">
        {/* back row */}
        <div className="gos-lr-backrow">
          <button type="button" className="gos-lr-ghost-btn" onClick={() => navigate('/contacts')}>
            <Ic name="chevL" size={15} /> Contacts list
          </button>
          {cd.id ? (
            <div className="gos-lr-leadid">
              Contact <span>{cd.id}</span>
            </div>
          ) : null}
        </div>

        {/* header card */}
        <div className="gos-lr-card gos-lr-header">
          <div className="gos-lr-header-top">
            <div className="gos-lr-header-l">
              <span className="gos-lr-ava" style={{ background: gosAvaColor(fullName) }}>
                {gosInitials(fullName)}
              </span>
              <div className="gos-lr-header-meta">
                <div className="gos-lr-name">{fullName}</div>
                <div className="gos-lr-subline">
                  <Ic name="briefcase" size={14} />
                  {[cd.title, cd.account].filter(Boolean).join(' · ') || '—'}
                  {cd.phone ? (
                    <>
                      <span className="gos-lr-dot">·</span>
                      <Ic name="phone" size={14} />
                      {cd.phone}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="gos-lr-actions">
              <button type="button" className="gos-lr-contact-btn" onClick={() => setCallOpen(true)}>
                <Ic name="phone" size={16} /> Call
              </button>
              <button type="button" className="gos-lr-contact-btn" onClick={() => setWaOpen(true)}>
                <Ic name="whatsapp" size={16} /> WhatsApp
              </button>
              <button
                type="button"
                className="gos-lr-contact-btn"
                onClick={() => setEmailOpen(true)}
                disabled={cd.emailOptOut === true}
              >
                <Ic name="mail" size={16} /> Mail
              </button>
              <button
                type="button"
                className="gos-lr-iconbtn gos-lr-iconbtn-bordered"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="More actions"
              >
                <Ic name="dots" size={18} />
              </button>
              {menuOpen ? (
                <>
                  <div className="gos-lr-menu-backdrop" onClick={() => setMenuOpen(false)} />
                  <div className="gos-lr-menu">
                    <button type="button" className="gos-lr-menu-item" onClick={() => { setMenuOpen(false); handleOpenEditForm(); }}>
                      <Ic name="edit" size={15} /> Edit Contact
                    </button>
                    <button
                      type="button"
                      className="gos-lr-menu-item"
                      onClick={() => { setMenuOpen(false); handleClickDataEnrichment(); }}
                      disabled={enrichedDataLoading}
                    >
                      <Ic name="zap" size={15} />
                      {cd.isEnriched || isEnrichedFlag ? 'Refresh Enriched Data' : 'Get Enriched Data'}
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {chips.length ? (
            <div className="gos-lr-chips">
              {chips.map((c, i) => (
                <span className="gos-lr-chip" key={i}>
                  <Ic name={c.icon} size={14} /> {c.label}
                </span>
              ))}
            </div>
          ) : null}
          <ContactTagEditor contact={cd} />
        </div>

        {/* tabs */}
        <div className="gos-lr-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={'gos-lr-tab' + (activeTab === t.id ? ' active' : '')}
              onClick={() => setActiveTab(t.id)}
            >
              <Ic name={t.icon} size={15} /> {t.label}
              {t.count != null ? <span className="gos-lr-count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {/* body */}
        {activeTab === 'overview' ? (
          overview
        ) : (
          <div className="gos-lr-2col">
            <div className="gos-lr-main">{tabBody}</div>
            {detailsPanel}
          </div>
        )}
      </div>

      {callOpen && <LeadCallModal lead={contactLocalData} onClose={() => setCallOpen(false)} />}
      {waOpen && <LeadWhatsAppModal lead={contactLocalData} onClose={() => setWaOpen(false)} />}
      {emailOpen && (
        <ComposeEmail
          localData={contactLocalData}
          module={'Contact'}
          handleClose={() => setEmailOpen(false)}
          handleChange={handleChange}
        />
      )}
      {showPopupContact && (
        <Enrichment
          handleClose={closePopupContact}
          localData={contactLocalData}
          enrichedData={conatctEnrichedData}
          handleSave={handleUpdateForm}
          loading={loading}
        />
      )}
    </>
  );
};

export default ViewContact;
